import { SITE_NAME } from './brand';
import { getLangPref, setLangPref } from './user-preferences';

import enLocale from '@/locales/en.json';
import idLocale from '@/locales/id.json';

// =============================================================================
// Part 1: Locale / t() system (used by interactive UI — ~50 lines, stays here)
// =============================================================================

type Locale = 'en' | 'id';

const locales: Record<Locale, Record<string, string>> = { en: enLocale, id: idLocale };

let currentLocale: Locale = 'en';

/** Sets the active locale and persists it to a cookie */
export function setLocale(locale: Locale) {
  currentLocale = locale;
  setLangPref(locale);
}

/** Returns the current locale, falling back to cookie or default */
export function getLocale(): Locale {
  if (typeof window !== 'undefined') {
    const stored = getLangPref() as Locale;
    if (stored && locales[stored]) return stored;
  }
  return currentLocale;
}

/** Translates a key to the current locale, with optional parameter interpolation */
export function t(key: string, params?: Record<string, string | number>): string {
  const locale = getLocale();
  let value = locales[locale]?.[key] || locales.en[key] || key;

  // Auto-interpolate {siteName} from brand config
  value = value.replace('{siteName}', SITE_NAME);

  if (params) {
    for (const [k, v] of Object.entries(params)) {
      value = value.replace(`{${k}}`, String(v));
    }
  }

  return value;
}

/** Returns the list of available UI locales with display labels */
export function getAvailableLocales(): { code: string; label: string }[] {
  const langMap = ensureLangMap();
  return [...langMap.values()]
    .filter(l => l.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(l => ({ code: l.code, label: l.label }));
}

// =============================================================================
// Barrel re-exports — preserves all 40 exports, zero consumer changes
// =============================================================================

export { YEAR } from './i18n-helpers';
export { interpolateTemplate, ui, uiFb, getPhraseEntry, getTemplateStr, getLanguagesForCountry, getLangLabel, getLangLabelEn, isRtlLang, toHreflang, getIntroText } from './i18n-helpers';
export { getCountryNameEntry, getCountryNamesByCode, getCityNameEntry, getCityNamesByIdRecord } from './i18n-helpers';

export type { CountrySlugEntry } from './slug-country';
export { getSlugRegistry, getSlugEntry, getAlternateLanguages, getSlugForCountry, getSlugForCountryYear, getAllLegacyCountrySlugs, matchLegacyCountrySlug } from './slug-country';

export type { UniversitySlugEntry } from './slug-university';
export { getAllUniversitySlugs, getUniversitySlugEntry, getAlternateUniversityLanguages, getUniversitySlugForId } from './slug-university';

export type { CitySlugEntry } from './slug-city';
export { getCitySlugForId, getCitySlugEntry, getAllCitySlugs, getAlternateCityLanguages } from './slug-city';

export type { CountryLensSlugEntry } from './slug-country-lens';
export { getAllCountryLensSlugs, getCountryLensSlugEntry, getAlternateCountryLensLanguages, getCountryLensSlugForId } from './slug-country-lens';

export type { EvergreenSlugEntry } from './slug-evergreen';
export { registerEvergreenProvider, getEvergreenSlugEntry, getAllEvergreenSlugs, getAlternateEvergreenLanguages } from './slug-evergreen';

// =============================================================================
// Cross-registry functions (depend on all registries — stay here)
// =============================================================================

import { getAllHubSlugs, parseHubSlug, getMonthDisplay } from './hub-pages';
import { _getUniversityHubSlugCache, ensureUniversityHubBuilt, getUniversityHubSlugEntry as _getUniversityHubSlugEntry, getAllUniversityHubSlugs, getUniversityHubSlugForCountry } from './slug-university-hub';
import { _getSlugRegistryCache, ensureBuilt, getSlugEntry as _getSlugEntry, getSlugForCountry } from './slug-country';
import { _getUniSlugCache, ensureUniversityBuilt, getUniversitySlugEntry as _getUniversitySlugEntry } from './slug-university';
import { _getCitySlugCache, ensureCityBuilt, getCitySlugEntry as _getCitySlugEntry } from './slug-city';
import { _getCountryLensSlugCache, ensureCountryLensBuilt, getCountryLensSlugEntry as _getCountryLensSlugEntry } from './slug-country-lens';
import { _getEvergreenSlugCache, ensureEvergreenBuilt, getEvergreenSlugEntry as _getEvergreenSlugEntry } from './slug-evergreen';
import { getAllEventSlugs, getEventBySlug } from './event-slugs';
import { ui as _ui, getCountryNameEntry as _getCountryNameEntry, getCityNameEntry as _getCityNameEntry, getPhraseEntry as _getPhraseEntry, ensureLangMap } from './i18n-helpers';
import { getCities, getUniversities } from './data-source';

/** Returns all registered URL slugs for static page generation (countries + universities + cities + hubs + long-weekends).
 *  Throws at build time if any slug collision is detected between registries. */
export function getAllSlugs(): string[] {
  ensureBuilt();
  ensureUniversityBuilt();
  ensureCityBuilt();
  ensureCountryLensBuilt();
  ensureUniversityHubBuilt();

  const slugMap = new Map<string, string>(); // slug → registry name

  function register(slugs: string[], registryName: string) {
    for (const slug of slugs) {
      const existing = slugMap.get(slug);
      if (existing) {
        throw new Error(
          `Slug collision: "${slug}" is registered by both "${existing}" and "${registryName}". ` +
          `Each slug must be unique across all registries.`
        );
      }
      slugMap.set(slug, registryName);
    }
  }

  register(Object.keys(_getSlugRegistryCache()), 'country');
  register(Object.keys(_getUniSlugCache()), 'university');
  register(Object.keys(_getCitySlugCache()), 'city');
  register(Object.keys(_getCountryLensSlugCache()), 'country-lens');
  register(getAllHubSlugs(), 'hub');
  register(Object.keys(_getUniversityHubSlugCache()), 'university-hub');

  // Evergreen slugs (no year suffix) — registered by page type modules
  ensureEvergreenBuilt();
  register(Object.keys(_getEvergreenSlugCache()), 'evergreen');

  return Array.from(slugMap.keys());
}

/** Resolve any slug to its BCP 47 language code.
 *  Checks all registries: country, university, city, country-lens, evergreen.
 *  Falls back to 'en' for hub pages, long-weekends, and unknown slugs. */
export function getLangFromSlug(slug: string): string {
  const country = _getSlugEntry(slug);
  if (country) return country.lang;

  const uni = _getUniversitySlugEntry(slug);
  if (uni) return uni.lang;

  const city = _getCitySlugEntry(slug);
  if (city) return city.lang;

  const lens = _getCountryLensSlugEntry(slug);
  if (lens) return lens.lang;

  const uniHub = _getUniversityHubSlugEntry(slug);
  if (uniHub) return uniHub.lang;

  const evergreen = _getEvergreenSlugEntry(slug);
  if (evergreen) return evergreen.lang;

  return 'en';
}

/** Dump a JSON manifest of all pages for the sync-cal-pages script.
 *  Called once during build (from generateStaticParams). */
export function dumpPageManifest(): void {
  if (typeof process === 'undefined') return;
  try {
    // Lazy CommonJS require keeps `fs`/`path` out of the client bundle — this
    // build-only function is never reached in the browser. (almanak-uk8q)
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const fs = require('fs');
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const path = require('path');
    const dir = path.join(process.cwd(), '.next', 'cache');
    fs.mkdirSync(dir, { recursive: true });

    const rows: Array<Record<string, unknown>> = [];

    // --- Homepage (one per active language) ---
    for (const lang of ['en', 'id']) {
      rows.push({
        slug: lang === 'en' ? 'index' : `index-${lang}`,
        page_type: 'homepage',
        lang,
        entity_id: 'homepage',
        country_code: null,
        page_title: _ui('ui.home', lang),
        page_description: null,
        h2_title: null,
        short_title: _ui('ui.home', lang),
        parent_slug: null,
        data: {},
      });
    }

    // --- Country pages ---
    ensureBuilt();
    const slugRegistryCache = _getSlugRegistryCache();
    for (const [slug, entry] of Object.entries(slugRegistryCache)) {
      const full = _getSlugEntry(slug);
      const countryName = _getCountryNameEntry(entry.code, entry.lang);
      rows.push({
        slug,
        page_type: 'country',
        lang: entry.lang,
        entity_id: `country:${entry.code.toLowerCase()}`,
        country_code: entry.code,
        page_title: full?.pageTitle ?? slug,
        page_description: full?.pageDescription ?? null,
        h2_title: null,
        short_title: countryName.display,
        parent_slug: null,
        data: {},
      });
    }

    // --- City pages ---
    ensureCityBuilt();
    const cityCache = _getCitySlugCache();
    const cities = getCities();
    for (const [slug, entry] of Object.entries(cityCache)) {
      const full = _getCitySlugEntry(slug);
      const city = cities.find((c: { id: string }) => c.id === entry.cityId);
      const countryCode = city?.country ?? null;
      const parentSlug = countryCode ? getSlugForCountry(countryCode, entry.lang) : null;
      rows.push({
        slug,
        page_type: 'city',
        lang: entry.lang,
        entity_id: `city:${entry.cityId}`,
        country_code: countryCode,
        page_title: full?.pageTitle ?? slug,
        page_description: full?.pageDescription ?? null,
        h2_title: full?.h2Title ?? null,
        short_title: _getCityNameEntry(entry.cityId, entry.lang)?.display ?? entry.cityId,
        parent_slug: parentSlug || null,
        data: {},
      });
    }

    // --- University pages ---
    ensureUniversityBuilt();
    const uniCache = _getUniSlugCache();
    const unis = getUniversities();
    for (const [slug, entry] of Object.entries(uniCache)) {
      const full = _getUniversitySlugEntry(slug);
      const uni = unis.find((u: { id: string }) => u.id === entry.universityId);
      const countryCode = uni?.country ?? null;
      const parentSlug = countryCode ? getSlugForCountry(countryCode, entry.lang) : null;
      rows.push({
        slug,
        page_type: 'university',
        lang: entry.lang,
        entity_id: `university:${entry.universityId}:${entry.academicYear}`,
        country_code: countryCode,
        page_title: full?.pageTitle ?? slug,
        page_description: full?.pageDescription ?? null,
        h2_title: full?.h2Title ?? null,
        short_title: uni ? (uni.abbrev || uni.name) : null,
        parent_slug: parentSlug || null,
        data: {},
      });
    }

    // --- Country-lens pages (religion + nature) ---
    ensureCountryLensBuilt();
    const lensCache = _getCountryLensSlugCache();
    for (const [slug, entry] of Object.entries(lensCache)) {
      const full = _getCountryLensSlugEntry(slug);
      const pageType = entry.lensType === 'religion' ? 'country-religion' : 'country-nature';
      const parentSlug = getSlugForCountry(entry.code, entry.lang) || null;
      const lensPhrase = _getPhraseEntry(entry.lensType === 'religion' ? 'religion_lens' : 'nature_lens', entry.lang);
      rows.push({
        slug,
        page_type: pageType,
        lang: entry.lang,
        entity_id: `${pageType}:${entry.code.toLowerCase()}`,
        country_code: entry.code,
        page_title: full?.pageTitle ?? slug,
        page_description: full?.pageDescription ?? null,
        h2_title: full?.h2Title ?? null,
        short_title: lensPhrase.display,
        parent_slug: parentSlug,
        data: {},
      });
    }

    // --- Hub pages (month, type, religion, nature) ---
    const hubSlugs = getAllHubSlugs();
    for (const slug of hubSlugs) {
      const hub = parseHubSlug(slug);
      if (!hub) continue;

      let pageType: string;
      let entityId: string;
      let shortTitle: string;
      switch (hub.kind) {
        case 'month':
          pageType = 'month-hub';
          entityId = `month-hub:${hub.month}`;
          shortTitle = getMonthDisplay(hub.month);
          break;
        case 'type':
          pageType = 'type-hub';
          entityId = `type-hub:${hub.eventType}`;
          shortTitle = hub.displayName;
          break;
        case 'religion':
          pageType = 'religion-hub';
          entityId = `religion-hub:${hub.subLens}`;
          shortTitle = hub.displayName;
          break;
        case 'nature':
          pageType = 'nature-hub';
          entityId = 'nature-hub:nature';
          shortTitle = 'Nature & Seasons';
          break;
      }

      rows.push({
        slug,
        page_type: pageType,
        lang: 'en',
        entity_id: entityId,
        country_code: null,
        page_title: hub.displayName,
        page_description: null,
        h2_title: null,
        short_title: shortTitle,
        parent_slug: null,
        data: {},
      });
    }

    // --- University hub pages ---
    for (const slug of getAllUniversityHubSlugs()) {
      const hubEntry = _getUniversityHubSlugEntry(slug);
      if (!hubEntry) continue;
      const parentSlug = getSlugForCountry(hubEntry.countryCode, hubEntry.lang) || null;
      const uniHubPhrase = _getPhraseEntry('university_calendars', hubEntry.lang);
      const uniHubTitle = (hubEntry.lang === 'id' || hubEntry.lang === 'ms')
        ? `${uniHubPhrase.display} ${hubEntry.countryName}`
        : `${hubEntry.countryName} ${uniHubPhrase.display}`;
      rows.push({
        slug: hubEntry.slug,
        page_type: 'university-hub',
        lang: hubEntry.lang,
        entity_id: `university-hub:${hubEntry.countryCode.toLowerCase()}`,
        country_code: hubEntry.countryCode,
        page_title: uniHubTitle,
        page_description: null,
        h2_title: null,
        short_title: uniHubPhrase.display,
        parent_slug: parentSlug,
        data: {},
      });
    }

    // --- Event detail pages ---
    const eventSlugs = getAllEventSlugs();
    for (const slug of eventSlugs) {
      const event = getEventBySlug(slug);
      if (!event) continue;
      const parentSlug = getSlugForCountry(event.country, 'en') || null;
      rows.push({
        slug: `events/${slug}`,
        page_type: 'event-detail',
        lang: 'en',
        entity_id: `event:${event.id}`,
        country_code: event.country,
        page_title: event.title,
        page_description: null,
        h2_title: null,
        short_title: event.title.includes(' — ') ? event.title.split(' — ')[0] : event.title,
        parent_slug: parentSlug,
        data: {
          eventType: event.type,
          subLens: event.subLens ?? null,
          eventGroup: event.eventGroup ?? null,
          dateStart: event.dateStart,
        },
      });
    }

    // --- Evergreen pages ---
    ensureEvergreenBuilt();
    const evergreenCache = _getEvergreenSlugCache();
    for (const [slug, entry] of Object.entries(evergreenCache)) {
      const countryCode = (entry.data.countryCode as string) ?? null;
      const parentSlug = countryCode
        ? getSlugForCountry(countryCode as Parameters<typeof getSlugForCountry>[0], entry.lang) || null
        : null;
      rows.push({
        slug,
        page_type: entry.pageType,
        lang: entry.lang,
        entity_id: entry.entityId ?? null,
        country_code: countryCode,
        page_title: entry.pageTitle,
        page_description: entry.pageDescription ?? null,
        h2_title: entry.h2Title ?? null,
        short_title: entry.h2Title
          ? entry.h2Title
              .replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}\uFE0E\uFE0F]/gu, '')
              .replace(/^\s+/, '')
              .replace(/\s*—\s.*$/, '')
            || entry.h2Title
          : null,
        parent_slug: parentSlug,
        data: entry.data,
      });
    }

    const outPath = path.join(dir, 'cal-pages-manifest.json');
    fs.writeFileSync(outPath, JSON.stringify(rows));
    if (process.env.NODE_ENV !== 'production') console.log(`📄 Page manifest: ${rows.length} pages → ${outPath}`);
  } catch (err) {
    console.error('⚠️ Page manifest dump failed:', err);
  }
}
